import { useState, useEffect, useCallback } from "react";
import { X, CaretLeft, CaretRight, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOut } from "@phosphor-icons/react";
import { cn } from "../lib/utils";
import AuthImage from "./AuthImage";

export function ImageLightbox({ images = [], initialIndex = 0, open, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => { setIndex(initialIndex); setZoom(1); }, [initialIndex, open]);

  const next = useCallback(() => { setIndex(i => (i + 1) % images.length); setZoom(1); }, [images.length]);
  const prev = useCallback(() => { setIndex(i => (i - 1 + images.length) % images.length); setZoom(1); }, [images.length]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "+" || e.key === "=") setZoom(z => Math.min(z + 0.25, 4));
      if (e.key === "-") setZoom(z => Math.max(z - 0.25, 0.5));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose, next, prev]);

  if (!open || images.length === 0) return null;
  const img = images[index];

  return (
    <div
      data-testid="image-lightbox"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10" onClick={e => e.stopPropagation()}>
        <p className="text-white/80 text-sm truncate max-w-[60%]">{img?.name || `Image ${index + 1}`}</p>
        <div className="flex items-center gap-2">
          <span className="text-white/60 text-xs">{index + 1} / {images.length}</span>
          <button onClick={() => setZoom(z => Math.min(z + 0.25, 4))} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-testid="lightbox-zoom-in">
            <MagnifyingGlassPlus size={18} />
          </button>
          <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-testid="lightbox-zoom-out">
            <MagnifyingGlassMinus size={18} />
          </button>
          <button onClick={() => setZoom(1)} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-testid="lightbox-reset-zoom">
            <ArrowsOut size={18} />
          </button>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-testid="lightbox-close">
            <X size={20} weight="bold" />
          </button>
        </div>
      </div>

      {/* Nav arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={e => { e.stopPropagation(); prev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white z-10 transition-colors"
            data-testid="lightbox-prev"
          >
            <CaretLeft size={24} weight="bold" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); next(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white z-10 transition-colors"
            data-testid="lightbox-next"
          >
            <CaretRight size={24} weight="bold" />
          </button>
        </>
      )}

      {/* Image */}
      <div className="flex items-center justify-center overflow-auto max-w-[90vw] max-h-[85vh]" onClick={e => e.stopPropagation()}>
        <AuthImage
          path={img?.url}
          alt={img?.name || "Image"}
          className="transition-transform duration-200 max-h-[82vh] object-contain select-none"
          style={{ transform: `scale(${zoom})` }}
          draggable={false}
          data-testid="lightbox-image"
        />
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 p-2 bg-black/50 rounded-lg max-w-[80vw] overflow-x-auto" onClick={e => e.stopPropagation()}>
          {images.map((im, i) => (
            <button
              key={i}
              onClick={() => { setIndex(i); setZoom(1); }}
              className={cn(
                "h-12 w-12 rounded-md overflow-hidden border-2 transition-all shrink-0",
                i === index ? "border-white scale-105" : "border-transparent opacity-60 hover:opacity-90"
              )}
              data-testid={`lightbox-thumb-${i}`}
            >
              <AuthImage path={im.url} alt={im.name} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
